import { Table, Text } from '@mantine/core';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  GpSurfacesMapSamplerCoverageOrganization,
  GpSurfacesMapSamplerCoverageTotals,
  GpSurfacesMapViewMode,
} from '../../../lib/types/gp-surfaces-map';
import { formatHaUnitFr } from '../../../lib/utils/gp-surfaces-format';

import { SamplerCoverageMetricText } from './sampler-coverage-metric-text';
import { SamplerCoverageMetricsCells } from './sampler-coverage-metrics-cells';
import { SamplerCoverageMetricsHead } from './sampler-coverage-metrics-head';

function sumCoverageTotals(
  rows: GpSurfacesMapSamplerCoverageTotals[],
): GpSurfacesMapSamplerCoverageTotals {
  const summed = rows.reduce(
    (acc, row) => ({
      actual_area: acc.actual_area + row.actual_area,
      projected_area: acc.projected_area + row.projected_area,
      combined_area: acc.combined_area + row.combined_area,
      projected_volume_t: acc.projected_volume_t + row.projected_volume_t,
      carbon_points: acc.carbon_points + row.carbon_points,
      analysis_count: acc.analysis_count + row.analysis_count,
      granulo_points: acc.granulo_points + row.granulo_points,
      granulo_analysis_count:
        acc.granulo_analysis_count + row.granulo_analysis_count,
      total_sampling_points:
        acc.total_sampling_points + row.total_sampling_points,
      total_analysis_count:
        acc.total_analysis_count + row.total_analysis_count,
    }),
    {
      actual_area: 0,
      projected_area: 0,
      combined_area: 0,
      projected_volume_t: 0,
      carbon_points: 0,
      analysis_count: 0,
      granulo_points: 0,
      granulo_analysis_count: 0,
      total_sampling_points: 0,
      total_analysis_count: 0,
    },
  );

  return {
    actual_area: Math.round(summed.actual_area * 10) / 10,
    projected_area: Math.round(summed.projected_area * 10) / 10,
    combined_area: Math.round(summed.combined_area * 10) / 10,
    projected_volume_t: Math.round(summed.projected_volume_t * 10) / 10,
    carbon_points: summed.carbon_points,
    analysis_count: summed.analysis_count,
    granulo_points: summed.granulo_points,
    granulo_analysis_count: summed.granulo_analysis_count,
    total_sampling_points: summed.total_sampling_points,
    total_analysis_count: summed.total_analysis_count,
  };
}

type GpSurfacesMapSamplerCoverageOrgTableProps = {
  organizations: GpSurfacesMapSamplerCoverageOrganization[];
  unassignedTotals: GpSurfacesMapSamplerCoverageTotals;
  viewMode: GpSurfacesMapViewMode;
};

export const GpSurfacesMapSamplerCoverageOrgTable: FC<
  GpSurfacesMapSamplerCoverageOrgTableProps
> = ({ organizations, unassignedTotals, viewMode }) => {
  const { t } = useTranslation();
  const grandTotals = useMemo(
    () =>
      sumCoverageTotals([
        ...organizations.map((organization) => organization.totals),
        unassignedTotals,
      ]),
    [organizations, unassignedTotals],
  );

  const renderTotalsRow = (
    key: string,
    label: string,
    totals: GpSurfacesMapSamplerCoverageTotals,
    options?: { dimmed?: boolean; emphasize?: boolean },
  ) => {
    const dimmed = options?.dimmed ?? false;
    const emphasize = options?.emphasize ?? false;
    const area =
      viewMode === 'with_projection'
        ? totals.combined_area
        : totals.actual_area;

    return (
      <Table.Tr key={key}>
        <Table.Td>
          <Text
            c={dimmed ? 'dimmed' : undefined}
            fw={emphasize ? 700 : 600}
            lineClamp={2}
            size="sm"
          >
            {label}
          </Text>
        </Table.Td>
        <Table.Td ta="right">
          <SamplerCoverageMetricText value={formatHaUnitFr(area)} />
        </Table.Td>
        <SamplerCoverageMetricsCells emphasizeTotals={emphasize} metrics={totals} />
      </Table.Tr>
    );
  };

  if (organizations.length === 0) {
    return (
      <Text c="dimmed" size="sm">
        {t('surfacesMap.samplerCoverage.empty')}
      </Text>
    );
  }

  return (
    <Table
      highlightOnHover
      horizontalSpacing="xs"
      striped
      style={{ tableLayout: 'fixed', width: '100%' }}
      verticalSpacing="xs"
      withTableBorder
    >
        <Table.Thead>
          <SamplerCoverageMetricsHead
            leadingHeaders={[
              {
                key: 'sampler',
                label: t('surfacesMap.samplerCoverage.columns.sampler'),
              },
              {
                key: 'area',
                label: t('surfacesMap.samplerCoverage.columns.area'),
                ta: 'right',
              },
            ]}
          />
        </Table.Thead>
        <Table.Tbody>
          {organizations.map((organization) =>
            renderTotalsRow(
              String(organization.id),
              organization.name,
              organization.totals,
            ),
          )}
          {renderTotalsRow(
            'unassigned',
            t('surfacesMap.samplerCoverage.unassignedRow'),
            unassignedTotals,
            { dimmed: true },
          )}
        </Table.Tbody>
        <Table.Tfoot>
          {renderTotalsRow(
            'grand-total',
            t('surfacesMap.samplerCoverage.totalRow'),
            grandTotals,
            { emphasize: true },
          )}
        </Table.Tfoot>
    </Table>
  );
};
